import React, { useEffect, useRef, useState } from 'react';
import { bubbleImageSrc } from '@/lib/bubbleImage';

const MOUTHS = {
  happy: 'M40 64 Q50 73 60 64',
  excited: 'M39 62 Q50 78 61 62 Q50 67 39 62 Z',
  curious: 'M46 66 Q50 69 54 66',
  sleepy: 'M44 66 Q50 68 56 66',
  sad: 'M42 69 Q50 63 58 69',
};

const hueFor = (tint = '#b48cff') => {
  const n = parseInt(tint.slice(1), 16);
  const r = (n >> 16) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  if (!d) return 0;
  const h = mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return Math.round(h * 60 + 360) % 360;
};

const useLook = (ref) => {
  const [look, setLook] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e) => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2), dy = e.clientY - (r.top + r.height / 2);
      const len = Math.hypot(dx, dy) || 1, k = Math.min(1, len / 400);
      setLook({ x: (dx / len) * 3.2 * k, y: (dy / len) * 2.6 * k });
    };
    window.addEventListener('pointermove', onMove);
    return () => window.removeEventListener('pointermove', onMove);
  }, [ref]);
  return look;
};

const useBlink = () => {
  const [closed, setClosed] = useState(false);
  useEffect(() => {
    let t;
    const loop = () => {
      t = setTimeout(() => { setClosed(true); setTimeout(() => setClosed(false), 140); loop(); }, 2400 + Math.random() * 3600);
    };
    loop();
    return () => clearTimeout(t);
  }, []);
  return closed;
};

const Eye = ({ cx, look, closed, lid }) => (
  <g style={{ transform: `scaleY(${closed ? 0.08 : 1})`, transformOrigin: `${cx}px 47px`, transition: 'transform 90ms ease-in' }}>
    <ellipse cx={cx} cy="47" rx="13.5" ry="15" fill="url(#bx-sclera)" />
    <g style={{ transform: `translate(${look.x}px, ${look.y}px)`, transition: 'transform 160ms ease-out' }}>
      <circle cx={cx} cy="48" r="10" fill="url(#bx-iris)" />
      <circle cx={cx} cy="48.5" r="5.2" fill="#05061a" />
      <ellipse cx={cx - 4} cy="42.5" rx="3.8" ry="3.2" fill="#fff" />
      <circle cx={cx + 3.8} cy="52.5" r="1.6" fill="#fff" opacity="0.9" />
      <circle cx={cx + 1.5} cy="44" r="0.7" fill="#fff" opacity="0.8" />
    </g>
    <ellipse cx={cx} cy="40" rx="10" ry="4" fill="#fff" opacity="0.12" />
    {lid > 0 && <path d={`M${cx - 13.5} 47 A13.5 15 0 0 1 ${cx + 13.5} 47 L${cx + 13.5} ${32 + lid} L${cx - 13.5} ${32 + lid} Z`} fill="rgba(150,130,255,0.6)" />}
  </g>
);

export const LivingBubble = ({ bubble, tint, mood = 'happy', away = false, className = '', testid = 'living-bubble' }) => {
  const ref = useRef(null);
  const look = useLook(ref);
  const closed = useBlink();
  const realistic = bubble?.style_v === 2 && (bubble?.image_url || bubble?.image_b64);
  const src = realistic ? bubbleImageSrc(bubble) : '/bubble-body.jpg';
  const filter = realistic ? undefined : `hue-rotate(${hueFor(tint || bubble?.tint) - 285}deg) saturate(1.15)`;
  const m = MOUTHS[mood] ? mood : 'happy';
  const lid = m === 'sleepy' ? 8 : 0;

  return (
    <div ref={ref} className={`relative aspect-square ${away ? 'opacity-0' : ''} ${className}`} data-testid={testid} data-mood={m}>
      {/* Outer glow halo */}
      <div className="absolute inset-[-8%] rounded-full pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(140,200,255,0.18) 40%, transparent 72%)',
      }} />
      {/* Soap-film body — fades at edges for translucency */}
      <div className="absolute inset-0 rounded-full overflow-hidden bx-breathe" style={{
        boxShadow: '0 20px 60px -20px rgba(120,160,255,0.6), inset 0 0 40px rgba(100,160,255,0.12)',
        maskImage: 'radial-gradient(circle at 50% 50%, black 52%, rgba(0,0,0,0.6) 68%, transparent 82%)',
        WebkitMaskImage: 'radial-gradient(circle at 50% 50%, black 52%, rgba(0,0,0,0.6) 68%, transparent 82%)',
      }}>
        <img src={src} alt={bubble?.name || 'bubble'} className={`h-full w-full object-cover scale-[1.22]${realistic ? ' bx-soap-hue' : ''}`} style={{ filter: realistic ? undefined : filter, opacity: 0.82 }} draggable={false} />
      </div>
      {/* Iridescent shimmer — primary, slow clockwise */}
      <div className="absolute inset-0 rounded-full pointer-events-none" style={{
        background: 'conic-gradient(from 20deg at 50% 50%, rgba(100,220,255,0.25) 0deg, rgba(210,120,255,0.18) 75deg, rgba(255,200,80,0.12) 160deg, rgba(80,255,200,0.18) 250deg, rgba(100,220,255,0.25) 360deg)',
        mixBlendMode: 'screen',
        maskImage: 'radial-gradient(circle, black 38%, transparent 65%)',
        WebkitMaskImage: 'radial-gradient(circle, black 38%, transparent 65%)',
        animation: 'bx-soap-spin 14s linear infinite',
      }} />
      {/* Iridescent shimmer — secondary, faster counter-clockwise, warm tones */}
      <div className="absolute inset-0 rounded-full pointer-events-none" style={{
        background: 'conic-gradient(from 200deg at 50% 50%, rgba(255,130,200,0.13) 0deg, rgba(120,200,255,0.15) 90deg, rgba(210,255,120,0.09) 190deg, rgba(255,170,100,0.13) 280deg, rgba(255,130,200,0.13) 360deg)',
        mixBlendMode: 'screen',
        maskImage: 'radial-gradient(circle, black 32%, transparent 60%)',
        WebkitMaskImage: 'radial-gradient(circle, black 32%, transparent 60%)',
        animation: 'bx-soap-spin-rev 9s linear infinite',
      }} />
      {/* Specular highlight — top-left glint */}
      <div className="absolute pointer-events-none" style={{
        top: '7%', left: '12%', width: '38%', height: '22%',
        background: 'radial-gradient(ellipse at 40% 50%, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.2) 40%, transparent 70%)',
        filter: 'blur(4px)',
        transform: 'rotate(-18deg)',
      }} />
      {/* Inner rim refraction ring */}
      <div className="absolute inset-[3%] rounded-full pointer-events-none" style={{
        boxShadow: 'inset 0 0 0 1.5px rgba(200,230,255,0.22), inset 0 -6px 18px rgba(180,140,255,0.18)',
      }} />
      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full pointer-events-none" aria-hidden="true">
        <defs>
          <radialGradient id="bx-iris" cx="45%" cy="65%" r="65%">
            <stop offset="0%" stopColor="#8fd0ff" /><stop offset="45%" stopColor="#3150d8" /><stop offset="100%" stopColor="#0b0f3a" />
          </radialGradient>
          <radialGradient id="bx-sclera" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor="#1c2160" /><stop offset="100%" stopColor="#070923" />
          </radialGradient>
        </defs>
        <Eye cx={34.5} look={look} closed={closed} lid={lid} />
        <Eye cx={65.5} look={look} closed={closed} lid={lid} />
        <ellipse cx="24" cy="63" rx="6.5" ry="3.6" fill="#ff6fae" opacity={m === 'sad' ? 0.15 : 0.45} />
        <ellipse cx="76" cy="63" rx="6.5" ry="3.6" fill="#ff6fae" opacity={m === 'sad' ? 0.15 : 0.45} />
        <path d={MOUTHS[m]} fill={m === 'excited' ? '#ff5f9e' : 'none'} stroke="#1a1240" strokeWidth="2.2" strokeLinecap="round" />
      </svg>
    </div>
  );
};

export default LivingBubble;
